import type { ReactNode } from "react";
import "./corpus.css";

/**
 * A deliberately minimal, dependency-free markdown renderer for corpus document bodies
 * (`CorpusArtifact.bodyMd`). This is NOT a CommonMark parser — it covers the subset a
 * vision/roadmap/decision/governance doc actually uses (headings, paragraphs, lists,
 * blockquotes, fenced code, and inline bold/italic/code/links) and falls back to plain text
 * for anything else, rather than either pulling in a markdown dependency for a handful of
 * artifacts per project or risking a mis-render of unsupported syntax.
 *
 * Never uses `dangerouslySetInnerHTML`: `bodyMd` is git-authored prose from a project's own
 * (operator-controlled, but still externally-sourced) repository, so it is treated as
 * untrusted content at this render boundary the same way any other externally-sourced text
 * would be (S-19) — every node here is a real React element, and a link's `href` is only
 * ever rendered as a clickable anchor when it matches a safe scheme (see `safeHref`);
 * anything else renders as inert text.
 */

function safeHref(href: string): string | undefined {
  if (/^(https?:|mailto:)/i.test(href) || href.startsWith("/") || href.startsWith("#")) {
    return href;
  }
  return undefined;
}

const INLINE_PATTERN = /\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+?)`|\[([^[\]]+)\]\(([^()\s]+)\)/g;

/** Renders one line/paragraph's worth of text, applying `**bold**`, `*italic*`, `` `code` ``,
 * and `[text](href)` inline. Deliberately non-recursive (no nested emphasis) — the corpus
 * kinds this renders are prose, not a markdown torture test. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let index = 0;
  INLINE_PATTERN.lastIndex = 0;
  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const [whole, bold, italic, code, linkText, linkHref] = match;
    const key = `${keyPrefix}-${index++}`;
    if (bold !== undefined) {
      nodes.push(<strong key={key}>{bold}</strong>);
    } else if (italic !== undefined) {
      nodes.push(<em key={key}>{italic}</em>);
    } else if (code !== undefined) {
      nodes.push(
        <code key={key} className="corpus-doc__inline-code">
          {code}
        </code>,
      );
    } else if (linkText !== undefined && linkHref !== undefined) {
      const href = safeHref(linkHref);
      nodes.push(
        href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>
        ) : (
          whole
        ),
      );
    }
    lastIndex = match.index + whole.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

type Block =
  | { type: "heading"; level: 1 | 2 | 3; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "blockquote"; lines: string[] }
  | { type: "code"; text: string };

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const UNORDERED_RE = /^[-*]\s+(.*)$/;
const ORDERED_RE = /^\d+\.\s+(.*)$/;
const QUOTE_RE = /^>\s?(.*)$/;
const RULE_RE = /^(-{3,}|\*{3,})$/;

/** Splits a corpus body into block-level nodes. Blank lines separate paragraphs; a fence
 * (```) captures everything up to its close verbatim (no inline formatting inside). */
function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      i++;
      continue;
    }

    if (line.trim().startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // consume closing fence
      blocks.push({ type: "code", text: codeLines.join("\n") });
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      const level = Math.min(heading[1].length, 3) as 1 | 2 | 3;
      blocks.push({ type: "heading", level, text: heading[2].trim() });
      i++;
      continue;
    }

    if (RULE_RE.test(line.trim())) {
      i++;
      continue;
    }

    if (QUOTE_RE.test(line)) {
      const quoteLines: string[] = [];
      while (i < lines.length && QUOTE_RE.test(lines[i])) {
        quoteLines.push(QUOTE_RE.exec(lines[i])![1]);
        i++;
      }
      blocks.push({ type: "blockquote", lines: quoteLines });
      continue;
    }

    if (UNORDERED_RE.test(line) || ORDERED_RE.test(line)) {
      const ordered = ORDERED_RE.test(line);
      const re = ordered ? ORDERED_RE : UNORDERED_RE;
      const items: string[] = [];
      while (i < lines.length && re.test(lines[i])) {
        items.push(re.exec(lines[i])![1]);
        i++;
      }
      blocks.push({ type: "list", ordered, items });
      continue;
    }

    // Paragraph: consume consecutive plain lines, joined with a space (soft-wrap).
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !HEADING_RE.test(lines[i]) &&
      !lines[i].trim().startsWith("```") &&
      !QUOTE_RE.test(lines[i]) &&
      !UNORDERED_RE.test(lines[i]) &&
      !ORDERED_RE.test(lines[i]) &&
      !RULE_RE.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: "paragraph", text: paraLines.join(" ") });
  }
  return blocks;
}

const HEADING_TAGS = { 1: "h4", 2: "h5", 3: "h6" } as const;

export function MarkdownLite({ text }: { text: string }) {
  const blocks = parseBlocks(text);
  return (
    <div className="corpus-doc__body">
      {blocks.map((block, i) => {
        const key = `block-${i}`;
        switch (block.type) {
          case "heading": {
            const Tag = HEADING_TAGS[block.level];
            return <Tag key={key}>{renderInline(block.text, key)}</Tag>;
          }
          case "paragraph":
            return <p key={key}>{renderInline(block.text, key)}</p>;
          case "list": {
            const ListTag = block.ordered ? "ol" : "ul";
            return (
              <ListTag key={key}>
                {block.items.map((item, itemIndex) => (
                  <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>
                ))}
              </ListTag>
            );
          }
          case "blockquote":
            return (
              <blockquote key={key}>
                {block.lines.map((line, lineIndex) => (
                  <p key={`${key}-${lineIndex}`}>{renderInline(line, `${key}-${lineIndex}`)}</p>
                ))}
              </blockquote>
            );
          case "code":
            return (
              <pre key={key} className="corpus-doc__code">
                <code>{block.text}</code>
              </pre>
            );
        }
      })}
    </div>
  );
}
